import {
  HttpPostRequestModel,
  HttpPutRequestModel,
  HttpDeleteRequestModel,
  HttpRequestModelType,
  __HttpRequestModel__,
} from 'shared/domain/httpRequest/httpRequestModel';
import type {
  IssueWorkflowOutDto,
  WorkflowStageOutDto,
  StageCreateInDto,
  StageUpdateInDto,
  StageReorderInDto,
} from 'shared/domain/workflow/types';
import {
  IStageUploader,
  UploadStage,
  UploadStageChanges,
  DeleteStage,
  ReorderStage,
} from 'serviceWorker/repository/workflow/uploadStage';
import type { IHttpRequestRunner } from 'shared/domain/httpRequest/httpRequestRunner';

export type StageCreateRequestModel = HttpPostRequestModel<
  {
    stageCreateDto: StageCreateInDto;
    workflowId: string;
    projectId: string;
    uniqueId: string;
  },
  HttpRequestModelType.workflowStage
>;

export type StageUpdateRequestModel = HttpPutRequestModel<
  {
    stageUpdateDto: StageUpdateInDto;
    stageId: string;
    workflowId: string;
    projectId: string;
    uniqueId: string;
  },
  HttpRequestModelType.workflowStage
>;

export type StageDeleteRequestModel = HttpDeleteRequestModel<
  {
    stageId: string;
    workflowId: string;
    projectId: string;
    uniqueId: string;
  },
  HttpRequestModelType.workflowStage
>;

export type StageReorderRequestModel = HttpPutRequestModel<
  {
    stageReorderDto: StageReorderInDto;
    stageId: string;
    workflowId: string;
    projectId: string;
    uniqueId: string;
  },
  HttpRequestModelType.workflowStageReorder
>;

export class StageRequestRunner implements IHttpRequestRunner {
  constructor(
    private stageUploader: IStageUploader,
    private uploadStageUseCase: UploadStage,
    private uploadStageChangesUseCase: UploadStageChanges,
    private deleteStageUseCase: DeleteStage,
    private reorderStageUseCase: ReorderStage,
    /** Stages are stored inside their workflow: sync the workflows after a stage change. */
    private syncWorkflows: () => void
  ) {}

  async execute(
    request:
      | __HttpRequestModel__
      | StageCreateRequestModel
      | StageUpdateRequestModel
      | StageDeleteRequestModel
      | StageReorderRequestModel
  ): Promise<IssueWorkflowOutDto | WorkflowStageOutDto> {
    const result = await this.upload(request);
    this.syncWorkflows();
    return result;
  }

  private async upload(
    request:
      | __HttpRequestModel__
      | StageCreateRequestModel
      | StageUpdateRequestModel
      | StageDeleteRequestModel
      | StageReorderRequestModel
  ): Promise<IssueWorkflowOutDto | WorkflowStageOutDto> {
    if (request.entityType === HttpRequestModelType.workflowStageReorder) {
      return this.reorderStage(request as StageReorderRequestModel);
    }

    switch (request.method) {
      case 'POST': {
        return this.uploadStage(request as StageCreateRequestModel);
      }
      case 'PUT': {
        return this.uploadStageChanges(request as StageUpdateRequestModel);
      }
      case 'DELETE': {
        return this.deleteStage(request as StageDeleteRequestModel);
      }
      default: {
        throw new Error(`Unsupported method for stage: ${request.method}`);
      }
    }
  }

  private async uploadStage(
    request: StageCreateRequestModel
  ): Promise<IssueWorkflowOutDto | WorkflowStageOutDto> {
    return this.uploadStageUseCase(
      this.stageUploader,
      request.data.stageCreateDto,
      request.url
    );
  }

  private async uploadStageChanges(
    request: StageUpdateRequestModel
  ): Promise<IssueWorkflowOutDto | WorkflowStageOutDto> {
    return this.uploadStageChangesUseCase(
      this.stageUploader,
      request.data.stageUpdateDto,
      request.url
    );
  }

  private async deleteStage(
    request: StageDeleteRequestModel
  ): Promise<IssueWorkflowOutDto | WorkflowStageOutDto> {
    return this.deleteStageUseCase(this.stageUploader, request.url);
  }

  private async reorderStage(
    request: StageReorderRequestModel
  ): Promise<IssueWorkflowOutDto | WorkflowStageOutDto> {
    return this.reorderStageUseCase(
      this.stageUploader,
      request.data.stageReorderDto,
      request.url
    );
  }
}
